import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';

const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
const CHECK_INTERVAL = 60 * 1000; // Check every minute

/**
 * useTokenRefresh Hook
 *
 * Automatically refreshes access token before it expires
 * to maintain seamless user experience
 */
export function useTokenRefresh() {
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const tokenExpiresAt = useAuthStore(state => state.tokenExpiresAt);
    const refreshToken = useAuthStore(state => state.refreshToken);
    const refreshTokens = useAuthStore(state => state.refreshTokens);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!isAuthenticated || !tokenExpiresAt || !refreshToken) {
            // Clear any existing interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const checkAndRefresh = async () => {
            const now = Date.now();
            const timeUntilExpiry = tokenExpiresAt - now;

            // If token expires in less than threshold, refresh proactively
            if (timeUntilExpiry > 0 && timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
                const success = await refreshTokens();

                if (success) {
                    return;
                }
            }
        };

        // Check immediately
        checkAndRefresh();

        // Then check periodically
        intervalRef.current = setInterval(checkAndRefresh, CHECK_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isAuthenticated, tokenExpiresAt, refreshToken, refreshTokens]);
}
